import type { ComponentOrReferenceFormattingRule } from '@ardoq/data-model';
import type { Formatting, FormattingGroup } from './types';
import { getFilterGroups } from './getFilterGroups';
import type { I18nLocale } from '@ardoq/i18n';
import { isTruthy } from '@ardoq/common-helpers';
import { intersection } from 'lodash';

export const computeCommonFieldNames = (
  typeNames: string[],
  typeNameAndFieldsIndex: Map<string, string[]>
): Set<string> | null => {
  const fieldArrays = typeNames
    .map(name => typeNameAndFieldsIndex.get(name))
    .filter(isTruthy);

  if (fieldArrays.length === 0) return null;

  return new Set(intersection(...fieldArrays));
};

const ruleFilterGroupsEntry =
  (
    i18nLocale: I18nLocale,
    filterGroups: FormattingGroup[],
    invalidRulesFormatting: Partial<Record<string, Formatting>>,
    typeNameAndFieldsIndex: Map<string, string[]>,
    isNewConditionalFormatting: boolean
  ) =>
  (
    rule: ComponentOrReferenceFormattingRule
  ): [ruleId: string, filterGroups: FormattingGroup[]] => {
    const { __id: ruleId, typeNames } = rule;
    const ruleFilterGroups = getFilterGroups({
      i18nLocale,
      filterGroups,
      invalidFormattingRuleFilter: invalidRulesFormatting[ruleId],
      typeNames,
      isNewConditionalFormatting,
    });

    if (!typeNames || typeNames.length === 0) {
      return [ruleId, ruleFilterGroups];
    }

    if (ruleFilterGroups.length < 2) {
      const [defaultFilterGroup] = ruleFilterGroups;
      return [ruleId, [defaultFilterGroup]];
    }

    const [
      defaultFilterGroup,
      { label: fieldFiltersCategoryLabel, filters: allFieldFilters },
    ] = ruleFilterGroups;

    const commonFieldNames = computeCommonFieldNames(
      typeNames,
      typeNameAndFieldsIndex
    );
    if (commonFieldNames === null) {
      // No valid types found in the index — don't restrict fields so the rule
      // still renders with full UI (same as old behavior for a single deleted type).
      return [ruleId, ruleFilterGroups];
    }

    const invalidFormatting = invalidRulesFormatting[ruleId];

    const fieldFilters = allFieldFilters.filter(
      ({ id }) =>
        commonFieldNames.has(id) ||
        // Keep invalid field in the list so an already selected but now unavailable
        // field can still be displayed/edited instead of disappearing from the UI.
        id === invalidFormatting?.id
    );
    const fieldFilterGroup = {
      label: fieldFiltersCategoryLabel,
      filters: fieldFilters,
    };
    const newRuleFilterGroups = [defaultFilterGroup, fieldFilterGroup];
    return [ruleId, newRuleFilterGroups];
  };

type FilterGroupsMapArgs = {
  i18nLocale: I18nLocale;
  formattingRules: ComponentOrReferenceFormattingRule[];
  filterGroups: FormattingGroup[];
  invalidRulesFormatting: Partial<Record<string, Formatting>>;
  typeNameAndFieldsIndex: Map<string, string[]>;
  isNewConditionalFormatting: boolean;
};
const filterGroupsMap = ({
  i18nLocale,
  formattingRules,
  filterGroups,
  invalidRulesFormatting,
  typeNameAndFieldsIndex,
  isNewConditionalFormatting,
}: FilterGroupsMapArgs) =>
  Object.fromEntries(
    formattingRules.map(
      ruleFilterGroupsEntry(
        i18nLocale,
        filterGroups,
        invalidRulesFormatting,
        typeNameAndFieldsIndex,
        isNewConditionalFormatting
      )
    )
  );
export { filterGroupsMap };
